/** I11/I13 - the persisted shapes.
 *
 * Two artifacts leave the process: a `SaveFile`, which is a whole `GameState`
 * plus the two version stamps needed to read it back, and a `ReplayFile`, which
 * is a starting state, an ordered command list and the checkpoint hashes those
 * commands must reproduce (D2).
 *
 * Neither carries a camera, a selection, an animation clock or a scene handle.
 * That is I11, and `schema.ts` checks it rather than trusting it.
 */

import type { GameCommand, ExecuteError } from "../commands.js";
import type { StateHash } from "../hash/index.js";
import type { GameState } from "../state.js";
import { CURRENT_FORMAT_VERSION } from "../state.js";

/** Discriminators, so a file that is not one of ours is refused by name. */
export const SAVE_KIND = "realm.save";
export const REPLAY_KIND = "realm.replay";

/**
 * The save format version is the state's own `formatVersion`: one number, not
 * two. A second counter would immediately drift from the first, and the thing
 * a migration actually rewrites is the state.
 */
export const SAVE_FORMAT_VERSION: number = CURRENT_FORMAT_VERSION;

/**
 * The oldest version the migration chain starts from. 0 is "a bare canonical
 * state dump with no envelope" - what the headless harness and the dev console
 * produced before this format existed, and what `migrations/0001.ts` lifts.
 */
export const MIN_SAVE_FORMAT_VERSION = 0;

// ------------------------------------------------------------------- save

export interface SaveFile {
  readonly kind: typeof SAVE_KIND;
  readonly formatVersion: number;
  readonly contentVersion: string;
  readonly state: GameState;
}

/** The envelope's key set, checked exactly. Extra keys are where presentation
 *  state gets in, so there is no room for one. */
export const SAVE_KEYS = ["contentVersion", "formatVersion", "kind", "state"] as const;

// ----------------------------------------------------------------- replay

/**
 * Why a checkpoint carries `day` and `round` as well as a hash: when a replay
 * diverges, "hash mismatch at command 812" is not a lead. "Hash mismatch at day
 * 14, battle round 3" is.
 */
export type CheckpointKind = "start" | "turn" | "battleRound" | "end";

export interface Checkpoint {
  /** Commands executed before this hash was taken. 0 is the starting state. */
  readonly at: number;
  readonly kind: CheckpointKind;
  readonly day: number;
  /** Battle round, or 0 when no battle is in progress. */
  readonly round: number;
  readonly hash: StateHash;
}

/**
 * `initial` is a whole save rather than a seed because a replay has to be
 * re-runnable inside core, and core cannot reach the map generator (I1) - the
 * seed alone does not name a map. `rootSeed` is still stated at the top level,
 * where a reader looks for it, and validated against the state it came from.
 */
export interface ReplayFile {
  readonly kind: typeof REPLAY_KIND;
  readonly formatVersion: number;
  readonly contentVersion: string;
  readonly rootSeed: number;
  readonly initial: SaveFile;
  readonly commands: readonly GameCommand[];
  readonly checkpoints: readonly Checkpoint[];
}

export const REPLAY_KEYS = [
  "checkpoints", "commands", "contentVersion", "formatVersion", "initial", "kind", "rootSeed",
] as const;

// ------------------------------------------------------------------ errors

/**
 * Reading a file is exactly as fallible as executing a command, so it answers
 * the same way: a structured `Err`, never a throw (Section 3.2). A throw from
 * anything below `parseSave` means core produced a file core cannot read.
 */
export type PersistError =
  | { readonly kind: "MALFORMED"; readonly issues: readonly string[] }
  | { readonly kind: "UNSUPPORTED_VERSION"; readonly found: number; readonly supported: number }
  | { readonly kind: "MISSING_MIGRATION"; readonly from: number; readonly to: number }
  | { readonly kind: "PRESENTATION_FIELD"; readonly path: string; readonly key: string }
  | { readonly kind: "CONTENT_MISMATCH"; readonly expected: string; readonly found: string }
  | {
      readonly kind: "CHECKPOINT_MISMATCH";
      readonly index: number;
      readonly checkpoint: Checkpoint;
      readonly actual: StateHash;
    }
  | { readonly kind: "COMMAND_REFUSED"; readonly index: number; readonly error: ExecuteError };

export const malformedFile = (...issues: string[]): PersistError => ({ kind: "MALFORMED", issues });

/** A one-line rendering for a log or a dialog. */
export function persistErrorText(e: PersistError): string {
  switch (e.kind) {
    case "MALFORMED": return `Not a readable file: ${e.issues.join("; ")}`;
    case "UNSUPPORTED_VERSION":
      return `Save format ${String(e.found)} is newer than this build understands (${String(e.supported)}).`;
    case "MISSING_MIGRATION":
      return `No migration from format ${String(e.from)} to ${String(e.to)}.`;
    case "PRESENTATION_FIELD":
      return `Presentation field "${e.key}" at ${e.path} (I11: saves hold simulation state only).`;
    case "CONTENT_MISMATCH":
      return `Recorded against content ${e.found}, this build is ${e.expected}.`;
    case "CHECKPOINT_MISMATCH":
      return `Replay diverged at command ${String(e.index)} (day ${String(e.checkpoint.day)}, `
        + `round ${String(e.checkpoint.round)}): expected ${e.checkpoint.hash}, got ${e.actual}.`;
    case "COMMAND_REFUSED":
      return `Replay command ${String(e.index)} was refused: ${e.error.kind}.`;
  }
}
